import { useState, useEffect, useCallback, useRef } from 'react';
import { StreamingService, StreamRequest, GenerationData, PresetData, PresetConfig } from '../services/streaming-service';

interface UseStreamingState {
  grid: number[][];  
  currentGeneration: number;
  isStreaming: boolean;
  status: string;
  presets: PresetData[];
  categories: string[];
  filteredPresets: PresetData[];
  selectedPreset: string;
  selectedCategory: string;
  gridSize: number;
  maxGenerations: number;
  streamInterval: number;
  initializerType: 'Random' | 'Center' | 'Cross' | 'CenterSingle' | 'CenterCross' | 'LeftSingle' | 'LeftCross' | 'RightSingle' | 'RightCross' | 'Glider' | 'Pulsar' | 'RPentomino' | 'Beacon' | 'Toad' | 'Blinker';
  density: number;
  seedValue: number;
  gridScale: number;
  isLoadingPresets: boolean;
  presetsError: string | null;
}

const createEmptyGrid = (size: number): number[][] => {
  if (!Number.isInteger(size) || size < 1 || size > 1000) {
    console.error('Invalid grid size:', size);
    return [];
  }
  const grid: number[][] = [];
  for (let i = 0; i < size; i++) {
    const row: number[] = [];
    for (let j = 0; j < size; j++) {
      row.push(0);
    }
    grid.push(row);
  }
  return grid;
};

const createInitializerPreview = (size: number, initializerType: string, density: number, seedValue: number): number[][] => {
  const grid = createEmptyGrid(size);
  if (grid.length === 0) return grid;
  
  const center = Math.floor(size / 2);
  
  const setCell = (r: number, c: number, value: number): void => {
    if (r >= 0 && r < size && c >= 0 && c < size && grid[r] && grid[r][c] !== undefined) {
      grid[r][c] = value;
    }
  };
  
  switch (initializerType) {
    case 'CenterSingle':
      setCell(center, center, seedValue);
      break;
    case 'Center':
      // Compact cross pattern
      setCell(center, center, seedValue);
      setCell(center - 1, center, seedValue);
      setCell(center + 1, center, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center, center + 1, seedValue);
      break;
    case 'CenterCross':
      // 5-cell cross pattern (matches server CenterCrossInitializer)
      setCell(center, center, seedValue);
      setCell(center - 1, center, seedValue);
      setCell(center + 1, center, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center, center + 1, seedValue);
      break;
    case 'Cross':
      // Large cross
      for (let i = center - 5; i <= center + 5; i++) {
        setCell(center, i, seedValue);
        setCell(i, center, seedValue);
      }
      break;
    case 'LeftSingle':
      setCell(center, Math.floor(size / 4), seedValue);
      break;
    case 'LeftCross':
      const leftPos = Math.floor(size / 4);
      setCell(center, leftPos, seedValue);
      setCell(center - 1, leftPos, seedValue);
      setCell(center + 1, leftPos, seedValue);
      setCell(center, leftPos - 1, seedValue);
      setCell(center, leftPos + 1, seedValue);
      break;
    case 'RightSingle':
      setCell(center, Math.floor(3 * size / 4), seedValue);
      break;
    case 'RightCross':
      const rightPos = Math.floor(3 * size / 4);
      setCell(center, rightPos, seedValue);
      setCell(center - 1, rightPos, seedValue);
      setCell(center + 1, rightPos, seedValue);
      setCell(center, rightPos - 1, seedValue);
      setCell(center, rightPos + 1, seedValue);
      break;
    case 'Glider':
      // Classic glider pattern
      setCell(center - 1, center, seedValue);
      setCell(center, center + 1, seedValue);
      setCell(center + 1, center - 1, seedValue);
      setCell(center + 1, center, seedValue);
      setCell(center + 1, center + 1, seedValue);
      break;
    case 'Pulsar':
      // Pulsar pattern (simplified)
      const offsets = [-6, -1, 1, 6];
      offsets.forEach(dy => {
        offsets.forEach(dx => {
          setCell(center + dy, center + dx, seedValue);
        });
      });
      break;
    case 'RPentomino':
      // R-Pentomino
      setCell(center - 1, center, seedValue);
      setCell(center - 1, center + 1, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center, center, seedValue);
      setCell(center + 1, center, seedValue);
      break;
    case 'Beacon':
      // Beacon oscillator
      setCell(center - 1, center - 1, seedValue);
      setCell(center - 1, center, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center + 1, center + 2, seedValue);
      setCell(center + 2, center + 1, seedValue);
      setCell(center + 2, center + 2, seedValue);
      break;
    case 'Toad':
      // Toad oscillator
      setCell(center, center - 1, seedValue);
      setCell(center, center, seedValue);
      setCell(center, center + 1, seedValue);
      setCell(center + 1, center - 2, seedValue);
      setCell(center + 1, center - 1, seedValue);
      setCell(center + 1, center, seedValue);
      break;
    case 'Blinker':
      // Blinker oscillator
      setCell(center, center - 1, seedValue);
      setCell(center, center, seedValue);
      setCell(center, center + 1, seedValue);
      break;
    case 'Acorn':
      // Acorn methuselah - 7 cells, complex evolution
      setCell(center, center - 3, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center - 1, center - 2, seedValue);
      setCell(center + 1, center, seedValue);
      setCell(center + 1, center + 1, seedValue);
      setCell(center + 1, center + 2, seedValue);
      setCell(center + 1, center + 3, seedValue);
      break;
    case 'Diehard':
      // Diehard - dies after 130 generations
      setCell(center, center - 3, seedValue);
      setCell(center, center - 2, seedValue);
      setCell(center + 1, center - 2, seedValue);
      setCell(center + 1, center + 2, seedValue);
      setCell(center + 1, center + 3, seedValue);
      setCell(center + 1, center + 4, seedValue);
      setCell(center - 1, center + 3, seedValue);
      break;
    case 'GosperGun':
      // Gosper Glider Gun - simplified (left side only)
      // Block 1
      setCell(center, center - 17, seedValue);
      setCell(center, center - 16, seedValue);
      setCell(center + 1, center - 17, seedValue);
      setCell(center + 1, center - 16, seedValue);
      // Left part
      setCell(center, center - 7, seedValue);
      setCell(center - 1, center - 7, seedValue);
      setCell(center + 1, center - 7, seedValue);
      setCell(center - 2, center - 6, seedValue);
      setCell(center + 2, center - 6, seedValue);
      setCell(center - 3, center - 5, seedValue);
      setCell(center + 3, center - 5, seedValue);
      setCell(center - 3, center - 4, seedValue);
      setCell(center + 3, center - 4, seedValue);
      setCell(center, center - 3, seedValue);
      setCell(center - 2, center - 2, seedValue);
      setCell(center + 2, center - 2, seedValue);
      setCell(center - 1, center - 1, seedValue);
      setCell(center, center - 1, seedValue);
      setCell(center + 1, center - 1, seedValue);
      setCell(center, center, seedValue);
      break;
    case 'LWSS':
      // Lightweight Spaceship - moves right
      setCell(center - 1, center - 1, seedValue);
      setCell(center - 1, center + 2, seedValue);
      setCell(center, center - 2, seedValue);
      setCell(center + 1, center - 2, seedValue);
      setCell(center + 1, center + 2, seedValue);
      setCell(center + 2, center - 2, seedValue);
      setCell(center + 2, center - 1, seedValue);
      setCell(center + 2, center, seedValue);
      setCell(center + 2, center + 1, seedValue);
      break;
    case 'Random':
      // Random distribution based on density
      for (let i = 0; i < size; i++) {
        for (let j = 0; j < size; j++) {
          if (Math.random() < density) {
            grid[i][j] = Math.floor(Math.random() * seedValue) + 1;
          }
        }
      }
      break;
    default:
      // Default: center single
      setCell(center, center, seedValue);
  }
  
  return grid;
};

const initialState: UseStreamingState = {
  grid: createEmptyGrid(30),
  currentGeneration: 0,
  isStreaming: false,
  status: 'Ready',
  presets: [],
  categories: ['All'],
  filteredPresets: [],
  selectedPreset: 'standard',
  selectedCategory: 'Base',
  gridSize: 30,
  maxGenerations: 100,
  streamInterval: 500,
  initializerType: 'Center',
  density: 0.2,
  seedValue: 128,
  gridScale: 1,
  isLoadingPresets: false,
  presetsError: null,
};

export const useStreaming = () => {
  const [state, setState] = useState<UseStreamingState>(initialState);
  const streamingServiceRef = useRef(new StreamingService());
  const connectionIdRef = useRef<string>('');

  // Initialize grid with initializer preview on mount
  const gridInitializedRef = useRef(false);
  useEffect(() => {
    if (gridInitializedRef.current) return;
    gridInitializedRef.current = true;
    if (state.gridSize < 10 || state.gridSize > 100) {
      console.warn('Grid size out of range, ignoring:', state.gridSize);
      return;
    }
    const grid = createInitializerPreview(state.gridSize, state.initializerType, state.density, state.seedValue);
    console.log('🔧 Grid initialized with preview:', state.initializerType, state.gridSize, 'x', state.gridSize);
    setState(prev => ({ ...prev, grid }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update grid preview when initializer settings change (not while streaming)
  useEffect(() => {
    if (state.isStreaming || !gridInitializedRef.current) return;
    const grid = createInitializerPreview(state.gridSize, state.initializerType, state.density, state.seedValue);
    console.log('🎯 Initializer preview updated:', state.initializerType);
    setState(prev => ({ ...prev, grid, currentGeneration: 0 }));
  }, [state.initializerType, state.density, state.seedValue, state.gridSize, state.isStreaming]);

  // Log isStreaming changes
  useEffect(() => {
    console.log('📡 isStreaming changed to:', state.isStreaming, 'connectionId:', connectionIdRef.current);
  }, [state.isStreaming]);

  // Load presets
  useEffect(() => {
    const loadPresets = async () => {
      setState(prev => ({ ...prev, isLoadingPresets: true, presetsError: null }));
      try {
        const fetchedPresets = await streamingServiceRef.current.fetchPresets();
        if (fetchedPresets && fetchedPresets.length > 0) {
          const catSet = new Set<string>();
          fetchedPresets.forEach(p => {
            if (p.category && p.category !== 'All') catSet.add(p.category);
          });
          const categories = Array.from(catSet);
          if (!categories.includes('Base')) {
            categories.unshift('Base');
          }

          const selectedCategory = 'Base';
          const filteredPresets = fetchedPresets.filter(p => p.category === selectedCategory);
          const selectedPreset = filteredPresets.length > 0 ? filteredPresets[0].name : 'standard';

          setState(prev => ({
            ...prev,
            presets: fetchedPresets,
            categories,
            selectedCategory,
            filteredPresets,
            selectedPreset,
            status: 'Ready',
            isLoadingPresets: false
          }));
        } else {
          setState(prev => ({
            ...prev,
            status: 'Error: No presets found',
            isLoadingPresets: false,
            presetsError: 'No presets found'
          }));
        }
      } catch (error) {
        console.error('Failed to load presets:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load presets';
        setState(prev => ({
          ...prev,
          status: 'Error loading presets',
          isLoadingPresets: false,
          presetsError: errorMessage
        }));
      }
    };
    loadPresets();
  }, []);

  const filterPresets = useCallback(() => {
    const filteredPresets = state.presets.filter(p => p.category === state.selectedCategory);
    if (!filteredPresets.find(p => p.name === state.selectedPreset)) {
      const selectedPreset = filteredPresets.length > 0 ? filteredPresets[0].name : 'standard';
      setState(prev => ({ ...prev, filteredPresets, selectedPreset }));
    } else {
      setState(prev => ({ ...prev, filteredPresets }));
    }
  }, [state.presets, state.selectedCategory, state.selectedPreset]);

  useEffect(() => {
    filterPresets();
  }, [filterPresets]);

  const getPresetConfig = useCallback((): PresetConfig => {
    const preset = state.presets.find(p => p.name === state.selectedPreset);
    const baseConfig = preset ? preset.config : {
      tier: 'Plain',
      engine: 'BaseTM1',
      atbashBonus: false,
      useLeap: false,
      symmetryMode: 'None',
      symmetryOrder: 0,
      enableDiagonalChecks: false,
      enforceDiagonalMirror: false,
      initializerType: 'Center',
      density: 0.2,
      seedValue: 128,
      colorMode: 'Mod8'
    };
    // Merge user-selected initializer settings
    return {
      ...baseConfig,
      initializerType: state.initializerType,
      density: state.density,
      seedValue: state.seedValue
    };
  }, [state.presets, state.selectedPreset, state.initializerType, state.density, state.seedValue]);

  const startStream = useCallback(async () => {
    // Check if already streaming
    if (connectionIdRef.current) {
      console.log('⚠️ Stream already running with connectionId:', connectionIdRef.current);
      return;
    }

    console.log('▶️ startStream: Setting isStreaming to true');
    setState(prev => {
      console.log('▶️ startStream setState callback: updating isStreaming from', prev.isStreaming, 'to true');
      return { ...prev, isStreaming: true, status: 'Connecting...' };
    });

    const presetConfig = getPresetConfig();

    const request: StreamRequest = {
      configuration: presetConfig,
      height: state.gridSize,
      width: state.gridSize,
      maxGenerations: state.maxGenerations,
      streamIntervalMs: state.streamInterval
    };

    try {
      const newConnectionId = await streamingServiceRef.current.startStream(
        request,
        (data: GenerationData) => {
          console.log('🎯 useStreaming: Received generation', data.generation, 'grid size:', data.grid?.length);
          const aliveCells = data.grid?.flat().filter(v => v > 0).length || 0;
          console.log('🎯 useStreaming: Alive cells:', aliveCells);
          setState(prev => {
            console.log('🎯 useStreaming: Updating state from gen', prev.currentGeneration, 'to', data.generation, 'isStreaming:', prev.isStreaming);
            return {
              ...prev,
              grid: data.grid.map(row => [...row]),
              currentGeneration: data.generation,
              status: `Generation ${data.generation}`,
              isStreaming: true
            };
          });
        },
        (error: string) => {
          console.error('🎯 useStreaming: Error callback:', error);
          setState(prev => ({
            ...prev,
            isStreaming: false,
            status: `Error: ${error}`
          }));
          connectionIdRef.current = '';
        },
        () => {
          console.log('🎯 useStreaming: Completed callback');
          setState(prev => ({
            ...prev,
            isStreaming: false,
            status: 'Completed'
          }));
          connectionIdRef.current = '';
        }
      );
      connectionIdRef.current = newConnectionId;
      console.log('✅ useStreaming: Stream started with connectionId:', connectionIdRef.current);
      console.log('✅ connectionIdRef.current is now:', connectionIdRef.current);
      console.log('✅ isStreaming should now be true, button should be enabled');
    } catch (error) {
      console.error('Stream error:', error);
      connectionIdRef.current = '';
      setState(prev => ({
        ...prev,
        status: `Error: ${error instanceof Error ? error.message : String(error)}`,
        isStreaming: false
      }));
    }
  }, [state.gridSize, state.maxGenerations, state.streamInterval, getPresetConfig]);

  const stopStream = useCallback(async () => {
    console.log('🛑 stopStream called, connectionId:', connectionIdRef.current);
    if (!connectionIdRef.current) {
      console.log('⚠️ Not streaming, nothing to stop');
      return;
    }
    console.log('🛑 Stopping stream');
    await streamingServiceRef.current.stopStream(connectionIdRef.current);
    connectionIdRef.current = '';
    setState(prev => ({ ...prev, isStreaming: false, status: 'Stopped' }));
    console.log('✅ Stream stopped');
  }, []);

  const resetGrid = useCallback(() => {
    console.log('🔄 resetGrid called');
    const grid = createEmptyGrid(state.gridSize);
    console.log('🔄 Resetting grid to', state.gridSize, 'x', state.gridSize);
    setState(prev => ({
      ...prev,
      grid,
      currentGeneration: 0,
      status: 'Ready',
      isStreaming: false
    }));
    console.log('✅ Grid reset complete');
  }, [state.gridSize]);

  const getCellColor = useCallback((value: number, colorMode?: string): string => {
    if (value === 0) return '#ffffff';
    
    // Check if 8-bit (256 color) mode
    const preset = state.presets.find(p => p.name === state.selectedPreset);
    const mode = colorMode || preset?.config?.colorMode || 'Mod8';
    
    if (mode === 'Full256') {
      // Full 256-color mode for 8-bit presets
      const hue = (value * 1.4) % 360;
      const saturation = 70 + (value % 30);
      const lightness = 40 + ((value * 7) % 25);
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }
    
    // Standard Mod8 colors
    const modVal = value % 8;
    if (modVal === 1) return '#3498db';
    if (modVal === 2) return '#2ecc71';
    if (modVal === 3) return '#f1c40f';
    if (modVal === 4) return '#e67e22';
    if (modVal === 5) return '#e74c3c';
    if (modVal === 6) return '#9b59b6';
    if (modVal === 7) return '#34495e';
    return '#1abc9c';
  }, [state.presets, state.selectedPreset]);

  const setSelectedCategory = useCallback((category: string) => {
    setState(prev => ({ ...prev, selectedCategory: category }));
  }, []);

  const setSelectedPreset = useCallback((preset: string) => {
    setState(prev => ({ ...prev, selectedPreset: preset }));
  }, []);

  const updateConfig = useCallback((updates: Partial<UseStreamingState>) => {
    setState(prev => {
      const newState = { ...prev, ...updates };
      // If grid size changed, recreate the grid
      if (updates.gridSize && updates.gridSize !== prev.gridSize) {
        newState.grid = createEmptyGrid(updates.gridSize);
        console.log('🔄 Grid size changed to', updates.gridSize);
      }
      return newState;
    });
  }, []);

  return {
    ...state,
    startStream,
    stopStream,
    resetGrid,
    getCellColor,
    setSelectedCategory,
    setSelectedPreset,
    updateConfig
  };
};
